import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import {
    Button, Card, CardHeader, CardBody, CardTitle,
    Container, Row, Col, Spinner, Form, FormGroup, Label, Input, Badge
} from "reactstrap";
import festivalSaleService from "../../services/api/festival-sale-service";

const SaleProposalReview = () => {
    const { targetId } = useParams();
    const [loading, setLoading] = useState(true);
    const [target, setTarget] = useState(null);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [saving, setSaving] = useState(false);
    const [bannerImage, setBannerImage] = useState("");
    const [bannerUploading, setBannerUploading] = useState(false);

    const fetchProposal = async () => {
        setLoading(true);
        try {
            const result = await festivalSaleService.getProposal(targetId);
            if (result && result.success) {
                setTarget(result.target);
                const campaign = result.target.campaign_id || {};
                const effectiveStart = result.target.edited_start || campaign.start_date;
                const effectiveEnd = result.target.edited_end || campaign.end_date;
                setStartDate(effectiveStart ? moment(effectiveStart).format("YYYY-MM-DDTHH:mm") : "");
                setEndDate(effectiveEnd ? moment(effectiveEnd).format("YYYY-MM-DDTHH:mm") : "");
                setBannerImage(result.target.edited_banner_image || campaign.banner_image || "");
            } else {
                setError(result.message || "This sale proposal could not be found.");
            }
        } catch (err) {
            console.error("Error fetching sale proposal:", err);
            setError("This sale proposal could not be found.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProposal();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetId]);

    const handleBannerChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = async () => {
            const base64Data = reader.result.split(',')[1];
            setBannerUploading(true);
            setError("");
            try {
                const result = await festivalSaleService.uploadBanner(targetId, base64Data);
                if (result && result.success) {
                    setBannerImage(result.url);
                } else {
                    setError(result.message || "Failed to upload banner image.");
                }
            } catch (err) {
                console.error("Error uploading banner image:", err);
                setError("An unexpected error occurred while uploading the banner image.");
            } finally {
                setBannerUploading(false);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleApprove = async () => {
        if (moment(startDate).isSameOrAfter(moment(endDate))) {
            setError("Start date must be before end date.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            // Converting to a real ISO string here (in the browser, where the local
            // timezone is whatever the publisher's machine is set to) avoids the
            // server re-interpreting a bare "YYYY-MM-DDTHH:mm" string in ITS OWN
            // timezone (which is UTC on staging/prod) — that mismatch is what caused
            // approved sales to land ~5.5 hours off from what was actually picked.
            const result = await festivalSaleService.approveAndSave(targetId, {
                start_date: new Date(startDate).toISOString(),
                end_date: new Date(endDate).toISOString(),
                banner_image: bannerImage
            });
            if (result && result.success) {
                setTarget(result.target);
                setMessage("Sale approved — it will go live automatically on the start date.");
            } else {
                setError(result.message || "Failed to approve this sale.");
            }
        } catch (err) {
            console.error("Error approving sale proposal:", err);
            setError(err.response?.data?.message || "Failed to approve this sale.");
        } finally {
            setSaving(false);
        }
    };

    const handleReject = async () => {
        setSaving(true);
        setError("");
        try {
            const result = await festivalSaleService.rejectProposal(targetId);
            if (result && result.success) {
                setTarget(result.target);
                setMessage("Sale proposal rejected.");
            } else {
                setError(result.message || "Failed to reject this sale.");
            }
        } catch (err) {
            console.error("Error rejecting sale proposal:", err);
            setError(err.response?.data?.message || "Failed to reject this sale.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Container fluid>
                <div className="text-center p-5"><Spinner color="primary" /></div>
            </Container>
        );
    }

    if (!target) {
        return (
            <Container fluid>
                <div className="alert alert-danger">{error || "This sale proposal could not be found."}</div>
            </Container>
        );
    }

    const campaign = target.campaign_id || {};
    const isDecided = target.status !== "pending";
    const minDateTime = moment().format("YYYY-MM-DDTHH:mm");
    const getVariants = () => {
        if (target.matched_variants && target.matched_variants.length) return target.matched_variants;
        if (Array.isArray(campaign.variants) && campaign.variants.length) return campaign.variants;
        if (campaign.currency && campaign.tier && campaign.amount) {
            return [{ currency: campaign.currency, tier: campaign.tier, amount: campaign.amount, display_amount: campaign.display_amount }];
        }
        return [];
    };
    const variants = getVariants();

    return (
        <Container fluid>
            <Row>
                <Col md={{ size: 8, offset: 2 }}>
                    <Card>
                        <CardHeader>
                            <CardTitle tag="h4">{campaign.title || "Festival Sale Proposal"}</CardTitle>
                            <Badge color={isDecided ? "default" : "warning"}>{target.status}</Badge>
                        </CardHeader>
                        <CardBody>
                            {error && <div className="alert alert-danger">{error}</div>}
                            {message && <div className="alert alert-success">{message}</div>}

                            {variants.map((v, i) => (
                                <p key={`${v.currency}_${v.tier}_${i}`}>
                                    <strong>{v.currency} — {v.tier}:</strong> {v.currency} {v.amount}
                                    {v.display_amount && v.display_amount !== v.amount ? (
                                        <span className="text-muted"> (was {v.currency} {v.display_amount})</span>
                                    ) : null}
                                </p>
                            ))}
                            <p><strong>Validity:</strong> {campaign.validity_in_months} months</p>

                            <Form>
                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Start Date & Time</Label>
                                            <Input
                                                type="datetime-local"
                                                value={startDate}
                                                min={minDateTime}
                                                disabled={isDecided}
                                                onChange={(e) => setStartDate(e.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>End Date & Time</Label>
                                            <Input
                                                type="datetime-local"
                                                value={endDate}
                                                min={startDate || minDateTime}
                                                disabled={isDecided}
                                                onChange={(e) => setEndDate(e.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={12}>
                                        <FormGroup>
                                            <Label>Banner Image</Label>
                                            {bannerImage && (
                                                <div className="mb-2">
                                                    <img src={bannerImage} alt="Sale banner preview" style={{ maxWidth: "100%", maxHeight: "150px" }} />
                                                </div>
                                            )}
                                            {!isDecided && (
                                                <>
                                                    <Input type="file" accept="image/*" onChange={handleBannerChange} />
                                                    {bannerUploading && <Spinner size="sm" className="ml-2" />}
                                                </>
                                            )}
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </Form>

                            {!isDecided && (
                                <div className="mt-3">
                                    <Button color="primary" className="mr-2" onClick={handleApprove} disabled={saving || bannerUploading}>
                                        {saving ? <Spinner size="sm" /> : "Approve & Save"}
                                    </Button>
                                    <Button color="danger" outline onClick={handleReject} disabled={saving}>
                                        {saving ? <Spinner size="sm" /> : "Reject"}
                                    </Button>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default SaleProposalReview;
